import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { customersAPI, usersAPI, documentsAPI } from '../lib/api';
import api from '../lib/api';
import { dnoAPI } from '../lib/api';
import { runProposalJob } from '../lib/proposalJob';
import { PostCreateInvoiceDialog } from '../components/PostCreateInvoiceDialog';
import { VariationOfContractDialog } from '../components/VariationOfContractDialog';
import { PROPERTY_TYPES, ROOF_TYPES, ROOF_ORIENTATIONS, PROJECT_STAGES, STATUS_DISPLAY_NAMES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '../components/ui/form';
import { ArrowLeft, Loader2, Save, Calendar, AlertCircle, Users, X, PoundSterling, CheckCircle, Building, House, Search, Upload, Zap, ChevronRight, Sparkles, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ProposalUploadCard } from '../components/ProposalUploadCard';
import { XeroQuoteImportCard } from '../components/XeroQuoteImportCard';
import { ProductInput } from '../components/ProductInput';
import { GenMeterSelect } from '../components/GenMeterSelect';

const customerSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(1, 'Phone number is required'),
  address: z.string().min(1, 'Address is required'),
  city: z.string().default(''),
  state: z.string().default(''),
  postcode: z.string().default(''),
  uprn: z.string().default(''),
  mpan: z.string().default(''),
  mpan_lookup_status: z.string().default(''),
  latitude: z.coerce.number().nullable().optional(),
  longitude: z.coerce.number().nullable().optional(),
  property_type: z.string().default('residential'),
  monthly_bill: z.coerce.number().min(0).default(0),
  monthly_usage: z.coerce.number().min(0).default(0),
  annual_usage: z.coerce.number().min(0).default(0),
  system_size: z.coerce.number().min(0).default(0),
  system_total_cost: z.coerce.number().min(0).default(0),
  vat_rate: z.coerce.number().default(0),
  roof_type: z.string().default(''),
  roof_orientation: z.string().default(''),
  // System type checkboxes
  system_type_solar: z.boolean().default(false),
  system_type_solar_battery: z.boolean().default(false),
  system_type_electrical: z.boolean().default(false),
  system_type_battery_only: z.boolean().default(false),
  system_type_other: z.boolean().default(false),
  system_type_ev_charger: z.boolean().default(false),
  electrical_description: z.string().default(''),
  other_description: z.string().default(''),
  ev_charger_description: z.string().default(''),
  ev_charger_manufacturer: z.string().default(''),
  ev_charger_model: z.string().default(''),
  ev_charger_quantity: z.coerce.number().min(0).default(0),
  // Equipment manufacturers and quantities
  panel_manufacturer: z.string().default(''),
  panel_model: z.string().default(''),
  panel_quantity: z.coerce.number().min(0).default(0),
  inverter_manufacturer: z.string().default(''),
  inverter_model: z.string().default(''),
  inverter_quantity: z.coerce.number().min(0).default(0),
  gen_meter_model: z.string().default(''),
  inverter_device_ref: z.string().default(''),
  inverter_device_data: z.any().nullable().optional(),
  battery_manufacturer: z.string().default(''),
  battery_model: z.string().default(''),
  battery_quantity: z.coerce.number().min(0).default(0),
  battery_device_ref: z.string().default(''),
  battery_device_data: z.any().nullable().optional(),
  consumer_unit_manufacturer: z.string().default(''),
  // Extra brand lines (multi-manufacturer jobs) — panel/inverter/battery/ev_charger
  additional_equipment: z.array(z.object({
    category: z.string().default(''),
    manufacturer: z.string().default(''),
    model: z.string().default(''),
    quantity: z.coerce.number().min(0).default(0),
    warranty_years: z.string().default(''),
  })).default([]),
  // Free-text bullet list of extras (e.g. scaffolding, bird mesh, optimisers)
  additional_extras: z.string().default(''),
  cutout_image_url: z.string().default(''),
  // Existing on-site technologies (EV charger / inverter / battery / other)
  // the customer already has — retain vs replace + make/model/size/qty + photo.
  existing_technologies: z.array(z.object({
    type: z.string().default(''),
    type_other: z.string().default(''),
    action: z.string().default('retain'),
    make: z.string().default(''),
    model: z.string().default(''),
    size: z.string().default(''),
    quantity: z.coerce.number().min(0).default(1),
    image_url: z.string().default(''),
  })).default([]),
  // Commercial client details
  is_commercial: z.boolean().default(false),
  company_name: z.string().default(''),
  company_registration: z.string().default(''),
  vat_number: z.string().default(''),
  // Installation dates
  provisional_install_date: z.string().nullable().optional(),
  confirmed_install_date: z.string().nullable().optional(),
  installation_days: z.coerce.number().min(1).default(1),
  notes: z.string().default(''),
  status: z.string().default('lead'),
  assigned_user_ids: z.array(z.string()).default([]),
  referral_source: z.string().default(''),
  referred_by_name: z.string().default(''),
  // Payment fields - Deposit
  deposit_amount: z.coerce.number().min(0).default(0),
  deposit_due_date: z.string().nullable().optional(),
  deposit_paid_date: z.string().nullable().optional(),
  // Payment fields - Final Bill
  final_bill_amount: z.coerce.number().min(0).default(0),
  final_bill_due_date: z.string().nullable().optional(),
  final_bill_paid_date: z.string().nullable().optional(),
  // Finance fields
  on_finance: z.boolean().default(false),
  finance_amount: z.coerce.number().min(0).default(0),
  pay_in_stages: z.boolean().default(false),
  stage_payments_draft: z.array(z.object({
    label: z.string().default(''),
    amount: z.coerce.number().default(0),
    due_date: z.string().default(''),
  })).default([]),
  portal_emails_enabled: z.boolean().default(true),
  source_xero_quote_id: z.string().default(''),
}).superRefine((d, ctx) => {
  // What the customer is having must be selected.
  const anyType = d.system_type_solar || d.system_type_solar_battery ||
    d.system_type_electrical || d.system_type_battery_only ||
    d.system_type_other || d.system_type_ev_charger;
  if (!anyType) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['system_type_solar'],
      message: 'Select what the customer is having (e.g. Solar PV, Solar PV + Battery).' });
  }
  // Equipment make / model / quantity required per selected system type.
  const reqEquip = (prefix, label) => {
    if (!String(d[`${prefix}_manufacturer`] || '').trim())
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`${prefix}_manufacturer`], message: `${label} manufacturer is required` });
    if (!String(d[`${prefix}_model`] || '').trim())
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`${prefix}_model`], message: `${label} model is required` });
    if (!(Number(d[`${prefix}_quantity`]) > 0))
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [`${prefix}_quantity`], message: `${label} quantity is required` });
  };
  // Inverter is intentionally NOT required here — the user may legitimately
  // omit it. We instead confirm the omission with a prompt on save (onSubmit).
  if (d.system_type_solar) { reqEquip('panel', 'Panel'); }
  if (d.system_type_solar_battery) { reqEquip('panel', 'Panel'); reqEquip('battery', 'Battery'); }
  if (d.system_type_battery_only) { reqEquip('battery', 'Battery'); }
  if (d.system_type_ev_charger) { reqEquip('ev_charger', 'EV charger'); }
});

const defaultFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  postcode: '',
  uprn: '',
  mpan: '',
  mpan_lookup_status: '',
  latitude: null,
  longitude: null,
  property_type: 'residential',
  monthly_bill: 0,
  monthly_usage: 0,
  annual_usage: 0,
  system_size: 0,
  system_total_cost: 0,
  vat_rate: 0,
  roof_type: 'not_specified',
  roof_orientation: 'not_specified',
  system_type_solar: false,
  system_type_solar_battery: false,
  system_type_electrical: false,
  system_type_battery_only: false,
  system_type_other: false,
  system_type_ev_charger: false,
  electrical_description: '',
  other_description: '',
  ev_charger_description: '',
  ev_charger_manufacturer: '',
  ev_charger_model: '',
  ev_charger_quantity: 0,
  panel_manufacturer: '',
  panel_model: '',
  panel_quantity: 0,
  inverter_manufacturer: '',
  inverter_model: '',
  inverter_quantity: 0,
  gen_meter_model: '',
  inverter_device_ref: '',
  inverter_device_data: null,
  battery_manufacturer: '',
  battery_model: '',
  battery_quantity: 0,
  battery_device_ref: '',
  battery_device_data: null,
  consumer_unit_manufacturer: '',
  additional_extras: '',
  additional_equipment: [],
  cutout_image_url: '',
  existing_technologies: [{ type: '', type_other: '', action: 'retain', make: '', model: '', size: '', quantity: 1, image_url: '' }],
  is_commercial: false,
  company_name: '',
  company_registration: '',
  vat_number: '',
  provisional_install_date: '',
  confirmed_install_date: '',
  installation_days: 1,
  notes: '',
  status: 'lead',
  assigned_user_ids: [],
  referral_source: '',
  referred_by_name: '',
  // Payment defaults
  deposit_amount: 0,
  deposit_due_date: '',
  deposit_paid_date: '',
  final_bill_amount: 0,
  final_bill_due_date: '',
  final_bill_paid_date: '',
  on_finance: false,
  finance_amount: 0,
  pay_in_stages: false,
  stage_payments_draft: [],
  portal_emails_enabled: true,
  source_xero_quote_id: '',
};

const _fmtGBP = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Build a human-readable list of cost/equipment changes for the prompt dialog.
const computeEquipmentChanges = (original, data) => {
  const out = [];
  const items = [
    { k: 'panel', label: 'Panels' },
    { k: 'inverter', label: 'Inverter' },
    { k: 'battery', label: 'Battery' },
    { k: 'ev_charger', label: 'EV charger' },
  ];
  for (const { k, label } of items) {
    const oQty = original[`${k}_quantity`] || 0;
    const nQty = data[`${k}_quantity`] || 0;
    if (oQty !== nQty) out.push({ label: `${label} quantity`, before: String(oQty), after: String(nQty) });
    const oKit = `${original[`${k}_manufacturer`] || ''} ${original[`${k}_model`] || ''}`.trim();
    const nKit = `${data[`${k}_manufacturer`] || ''} ${data[`${k}_model`] || ''}`.trim();
    if (oKit !== nKit) out.push({ label: `${label} make/model`, before: oKit || '—', after: nKit || '—' });
  }
  if ((original.additional_extras || '') !== (data.additional_extras || '')) {
    out.push({ label: 'Additional extras', before: original.additional_extras || '—', after: data.additional_extras || '—' });
  }
  const _fmtEquip = (arr) => (Array.isArray(arr) ? arr : [])
    .map(l => `${l.quantity || 0}x ${l.manufacturer || ''} ${l.model || ''}`.trim())
    .filter(Boolean).join(', ');
  const oExtra = _fmtEquip(original.additional_equipment);
  const nExtra = _fmtEquip(data.additional_equipment);
  if (oExtra !== nExtra) out.push({ label: 'Additional equipment lines', before: oExtra || '—', after: nExtra || '—' });
  const oCost = parseFloat(original.system_total_cost) || 0;
  const nCost = parseFloat(data.system_total_cost) || 0;
  if (Math.abs(oCost - nCost) > 0.005) out.push({ label: 'Total cost', before: _fmtGBP(oCost), after: _fmtGBP(nCost) });
  return out;
};

// Extra brand lines for a hardware category (multi-manufacturer jobs).
const AdditionalEquipmentLines = ({ form, category, label, catManufacturers, catModels }) => {
  const all = form.watch('additional_equipment') || [];
  const lines = all.map((l, i) => ({ ...l, _idx: i })).filter((l) => l.category === category);
  const addLine = () => {
    const cur = form.getValues('additional_equipment') || [];
    form.setValue('additional_equipment', [...cur, { category, manufacturer: '', model: '', quantity: 0, warranty_years: '' }], { shouldDirty: true });
  };
  const removeLine = (idx) => {
    const cur = form.getValues('additional_equipment') || [];
    form.setValue('additional_equipment', cur.filter((_, i) => i !== idx), { shouldDirty: true });
  };
  const updateLine = (idx, key, value) => {
    const cur = [...(form.getValues('additional_equipment') || [])];
    cur[idx] = { ...cur[idx], [key]: value };
    form.setValue('additional_equipment', cur, { shouldDirty: true });
  };
  return (
    <div className="space-y-3 mt-3" data-testid={`additional-${category}-block`}>
      {lines.map((l) => (
        <div key={l._idx} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end pt-3 border-t border-dashed border-slate-300" data-testid={`additional-${category}-row-${l._idx}`}>
          <div className="md:col-span-2">
            <FormLabel className="text-xs text-slate-500">Additional {label} Manufacturer</FormLabel>
            <ProductInput
              value={l.manufacturer}
              onChange={(v) => updateLine(l._idx, 'manufacturer', v)}
              options={catManufacturers(category)}
              placeholder="Manufacturer"
              testId={`additional-${category}-manufacturer-${l._idx}`}
              nudgeLabel="Manufacturer not in catalog — please double-check the name."
            />
          </div>
          <div>
            <FormLabel className="text-xs text-slate-500">Model</FormLabel>
            <ProductInput
              value={l.model}
              onChange={(v) => updateLine(l._idx, 'model', v)}
              options={catModels(category, l.manufacturer)}
              placeholder="Model"
              testId={`additional-${category}-model-${l._idx}`}
              nudgeLabel="Model not in catalog — please double-check the model number."
            />
          </div>
          <div>
            <FormLabel className="text-xs text-slate-500">Quantity</FormLabel>
            <Input
              type="number"
              min="0"
              value={l.quantity}
              onChange={(e) => updateLine(l._idx, 'quantity', Number(e.target.value))}
              data-testid={`additional-${category}-quantity-${l._idx}`}
            />
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <FormLabel className="text-xs text-slate-500">Warranty (yrs)</FormLabel>
              <Input
                type="number"
                min="0"
                value={l.warranty_years || ''}
                onChange={(e) => updateLine(l._idx, 'warranty_years', e.target.value)}
                placeholder="e.g. 10"
                data-testid={`additional-${category}-warranty-${l._idx}`}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-red-500 hover:bg-red-50 flex-shrink-0"
              onClick={() => removeLine(l._idx)}
              data-testid={`additional-${category}-remove-${l._idx}`}
              title="Remove this line"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-dashed text-slate-600"
        onClick={addLine}
        data-testid={`additional-${category}-add`}
      >
        <Plus className="w-4 h-4 mr-1" /> Add another {label.toLowerCase()}
      </Button>
    </div>
  );
};

const EXISTING_TECH_TYPES = [
  { value: 'ev_charger', label: 'EV Charger' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'battery', label: 'Battery' },
  { value: 'other', label: 'Other' },
];

// Captures technologies the customer already has on site, whether we're
// keeping or replacing each one, plus make/model/size/qty and an optional photo.
const ExistingTechnologiesSection = ({ form }) => {
  const items = form.watch('existing_technologies') || [];
  const [uploadingIdx, setUploadingIdx] = useState(null);

  const addItem = () => {
    const cur = form.getValues('existing_technologies') || [];
    form.setValue('existing_technologies', [...cur, {
      type: '', type_other: '', action: 'retain', make: '', model: '', size: '', quantity: 1, image_url: '',
    }], { shouldDirty: true });
  };
  const removeItem = (idx) => {
    const cur = form.getValues('existing_technologies') || [];
    form.setValue('existing_technologies', cur.filter((_, i) => i !== idx), { shouldDirty: true });
  };
  const updateItem = (idx, key, value) => {
    const cur = [...(form.getValues('existing_technologies') || [])];
    cur[idx] = { ...cur[idx], [key]: value };
    form.setValue('existing_technologies', cur, { shouldDirty: true });
  };
  const uploadImage = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const res = await dnoAPI.uploadAttachment(file, 'image');
      const url = res.data?.file_url || '';
      if (url) { updateItem(idx, 'image_url', url); toast.success('Photo attached'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingIdx(null);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="existing-tech-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
          <Zap className="w-5 h-5" />
          Existing Technologies On Site
        </CardTitle>
        <FormDescription>
          Record any equipment the customer already has (EV charger, inverter, battery, etc.) and whether we're retaining or replacing it.
        </FormDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="existing-tech-empty">No existing technologies recorded yet.</p>
        )}
        {items.map((item, idx) => (
          <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid={`existing-tech-row-${idx}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <FormLabel className="text-xs text-slate-500">Technology</FormLabel>
                <Select value={item.type || ''} onValueChange={(v) => updateItem(idx, 'type', v)}>
                  <SelectTrigger data-testid={`existing-tech-type-${idx}`}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXISTING_TECH_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {item.type === 'other' && (
                <div>
                  <FormLabel className="text-xs text-slate-500">Describe</FormLabel>
                  <Input
                    value={item.type_other || ''}
                    onChange={(e) => updateItem(idx, 'type_other', e.target.value)}
                    placeholder="e.g. Solar diverter"
                    data-testid={`existing-tech-type-other-${idx}`}
                  />
                </div>
              )}
              <div>
                <FormLabel className="text-xs text-slate-500">Retain or Replace?</FormLabel>
                <div className="flex gap-2 mt-1">
                  {['retain', 'replace'].map((a) => (
                    <Button
                      key={a}
                      type="button"
                      size="sm"
                      variant={item.action === a ? 'default' : 'outline'}
                      className="flex-1 capitalize"
                      onClick={() => updateItem(idx, 'action', a)}
                      data-testid={`existing-tech-action-${a}-${idx}`}
                    >
                      {a}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <FormLabel className="text-xs text-slate-500">Make</FormLabel>
                <Input value={item.make || ''} onChange={(e) => updateItem(idx, 'make', e.target.value)} placeholder="Make" data-testid={`existing-tech-make-${idx}`} />
              </div>
              <div>
                <FormLabel className="text-xs text-slate-500">Model</FormLabel>
                <Input value={item.model || ''} onChange={(e) => updateItem(idx, 'model', e.target.value)} placeholder="Model" data-testid={`existing-tech-model-${idx}`} />
              </div>
              <div>
                <FormLabel className="text-xs text-slate-500">Size</FormLabel>
                <Input value={item.size || ''} onChange={(e) => updateItem(idx, 'size', e.target.value)} placeholder="e.g. 7kW / 5kWh" data-testid={`existing-tech-size-${idx}`} />
              </div>
              <div>
                <FormLabel className="text-xs text-slate-500">Qty</FormLabel>
                <Input type="number" min="0" value={item.quantity ?? 1} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} data-testid={`existing-tech-quantity-${idx}`} />
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>{item.image_url ? 'Replace photo' : 'Attach photo'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid={`existing-tech-image-upload-${idx}`}
                    onChange={(e) => uploadImage(idx, e.target.files?.[0])}
                  />
                </label>
                {uploadingIdx === idx && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
                {item.image_url && (
                  <a href={item.image_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-emerald-700" data-testid={`existing-tech-image-link-${idx}`}>
                    <CheckCircle className="w-4 h-4" /> Photo attached
                  </a>
                )}
                {item.image_url && (
                  <img src={item.image_url} alt="Existing tech" className="w-10 h-10 rounded object-cover border border-slate-200" />
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={`text-red-500 hover:bg-red-50 ${items.length <= 1 ? 'invisible' : ''}`}
                onClick={() => removeItem(idx)}
                data-testid={`existing-tech-remove-${idx}`}
                title="Remove this item"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-dashed text-slate-600"
          onClick={addItem}
          data-testid="existing-tech-add"
        >
          <Plus className="w-4 h-4 mr-1" /> Add existing technology
        </Button>
      </CardContent>
    </Card>
  );
};

export const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [postcodeLooking, setPostcodeLooking] = useState(false);
  const [mpanLooking, setMpanLooking] = useState(false);
  const [ttrDialogTarget, setTtrDialogTarget] = useState(null); // 'inverter' | 'battery' | null
  const [uploadingCutout, setUploadingCutout] = useState(false);
  const [pendingCutout, setPendingCutout] = useState(null);
  const [postcodeResult, setPostcodeResult] = useState(null);
  // Post-create invoice prompt
  const [postCreateOpen, setPostCreateOpen] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);
  // Variation of Contract — opens after save when equipment changed vs snapshot
  const [variationDialogOpen, setVariationDialogOpen] = useState(false);
  const [variationCustomer, setVariationCustomer] = useState(null);
  const [changePromptOpen, setChangePromptOpen] = useState(false);
  // EV charger sub-board prompt — shown on save when a battery system is selected
  const [evSubBoardOpen, setEvSubBoardOpen] = useState(false);
  const evAnsweredRef = useRef(false);
  // Missing-inverter confirmation — shown on save if a PV/battery system has no inverter
  const [inverterPromptOpen, setInverterPromptOpen] = useState(false);
  const inverterAnsweredRef = useRef(false);
  const [jobNameOpen, setJobNameOpen] = useState(false);
  const [jobNameInput, setJobNameInput] = useState('');
  const jobNamedRef = useRef(false);
  const chosenJobNameRef = useRef('');
  // Email-change resend prompt (edit): offer to resend contract + invoice to new email
  const [emailResendOpen, setEmailResendOpen] = useState(false);
  const [emailResendNav, setEmailResendNav] = useState('/customers');
  const [emailResendBusy, setEmailResendBusy] = useState(false);
  const [changeSummary, setChangeSummary] = useState([]);
  // Solar PV proposal — held in memory until the customer record exists
  // (new-customer flow), then re-posted with `pre_extracted` so the PDF
  // gets attached + `mcs_solar_pv_data` persists WITHOUT a second Gemini call.
  const [pendingProposal, setPendingProposal] = useState(null);
  // One-time "Upload a proposal first?" prompt on the new-customer flow.
  // Defaults to true on mount when creating; dismissed by either action.
  const [proposalPromptOpen, setProposalPromptOpen] = useState(false);
  const proposalCardRef = useRef(null);
  useEffect(() => {
    // Only show the prompt for new customers, after the form has mounted
    // and we've confirmed there's no existing data being edited.
    if (!isEdit) {
      const t = setTimeout(() => setProposalPromptOpen(true), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isEdit]);

  const handlePostcodeLookup = async () => {
    const pc = form.getValues('postcode');
    if (!pc || pc.trim().length < 3) {
      toast.error('Please enter a valid postcode first');
      return;
    }
    setPostcodeLooking(true);
    try {
      const resp = await api.get('/postcodes/lookup', { params: { postcode: pc.trim() } });
      const data = resp.data;
      setPostcodeResult(data);
      if (data.postcode) form.setValue('postcode', data.postcode);
      if (data.city) form.setValue('city', data.city);
      if (data.county) form.setValue('state', data.county);
      if (data.latitude) form.setValue('latitude', data.latitude);
      if (data.longitude) form.setValue('longitude', data.longitude);
      toast.success(`Found: ${[data.ward, data.city, data.county].filter(Boolean).join(', ')} — enter house number/name below`);
    } catch (err) {
      setPostcodeResult(null);
      if (err.response?.status === 404) {
        toast.error('Postcode not found');
      } else {
        toast.error('Postcode lookup failed');
      }
    } finally {
      setPostcodeLooking(false);
    }
  };

  const form = useForm({
    resolver: zodResolver(customerSchema),
    defaultValues: defaultFormValues,
  });

  // Try to auto-retrieve the MPAN from ENA for the current address. On success
  // fills the MPAN field; on failure flags the address block so staff can enter
  // it manually (mpan_lookup_status = 'failed').
  const lookupMpan = async ({ silent = false } = {}) => {
    const address = form.getValues('address');
    const postcode = form.getValues('postcode');
    const uprn = form.getValues('uprn');
    if (!address && !postcode && !uprn) {
      if (!silent) toast.error('Enter an address or postcode first');
      return;
    }
    setMpanLooking(true);
    try {
      const { data } = await dnoAPI.mpanLookup({ address, postcode, uprn });
      if (data.found && data.mpan) {
        form.setValue('mpan', data.mpan);
        form.setValue('mpan_lookup_status', 'found');
        if (!silent) toast.success(`MPAN found: ${data.mpan}`);
      } else {
        form.setValue('mpan_lookup_status', 'failed');
        if (!silent) toast.warning('MPAN could not be retrieved from ENA — please enter it manually');
      }
    } catch {
      form.setValue('mpan_lookup_status', 'failed');
      if (!silent) toast.warning('MPAN lookup failed — please enter it manually');
    } finally {
      setMpanLooking(false);
    }
  };

  // Snapshot of the loaded customer so we can detect if the install dates
  // changed between load and save (used to trigger the post-save email prompt).
  const originalCustomerRef = useRef({});

  // Pre-populate from navigation state (e.g. from Postcode Checker)
  useEffect(() => {
    if (!isEdit && location.state) {
      const s = location.state;
      if (s.postcode) form.setValue('postcode', s.postcode);
      if (s.city) form.setValue('city', s.city);
      if (s.state) form.setValue('state', s.state);
      if (s.address) form.setValue('address', s.address);
      if (s.first_name) form.setValue('first_name', s.first_name);
      if (s.last_name) form.setValue('last_name', s.last_name);
      if (s.phone) form.setValue('phone', s.phone);
      if (s.whatsapp_number) form.setValue('whatsapp_number', s.whatsapp_number);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch confirmed date to handle provisional date
  const confirmedDate = form.watch('confirmed_install_date');
  const provisionalDate = form.watch('provisional_install_date');
  const assignedUserIds = form.watch('assigned_user_ids');

  // Watch system type checkboxes for conditional equipment visibility
  const watchSolar = form.watch('system_type_solar');
  const watchSolarBattery = form.watch('system_type_solar_battery');
  const watchElectrical = form.watch('system_type_electrical');
  const watchBatteryOnly = form.watch('system_type_battery_only');
  const watchOther = form.watch('system_type_other');
  const watchEvCharger = form.watch('system_type_ev_charger');

  // Product Catalog — powers the equipment manufacturer/model dropdowns
  const [productCatalog, setProductCatalog] = useState([]);
  useEffect(() => {
    api.get('/product-catalog')
      .then((r) => setProductCatalog(r.data?.items || []))
      .catch(() => setProductCatalog([]));
  }, []);
  const catManufacturers = (cat) =>
    Array.from(new Set(productCatalog.filter((c) => c.category === cat).map((c) => c.manufacturer).filter(Boolean)));
  const catModels = (cat, manu) =>
    Array.from(new Set(productCatalog
      .filter((c) => c.category === cat && (!manu || (c.manufacturer || '').toLowerCase() === (manu || '').toLowerCase()))
      .map((c) => c.model).filter(Boolean)));

  // Determine which equipment fields to show
  const showSolarPanel = watchSolar || watchSolarBattery;
  const showInverter = watchSolar || watchSolarBattery || watchBatteryOnly;
  const showBattery = watchSolarBattery || watchBatteryOnly;
  const showElectricalFreeText = watchElectrical;
  const showOtherFreeText = watchOther;
  const showEvCharger = watchEvCharger;
  // If no type selected, show all equipment by default
  const noTypeSelected = !watchSolar && !watchSolarBattery && !watchElectrical && !watchBatteryOnly && !watchOther && !watchEvCharger;
  const showAllEquipment = noTypeSelected;

  // Fetch available users for assignment
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await usersAPI.getAll();
        // Filter active users (API returns 'active' not 'is_active')
        setAvailableUsers(response.data.filter(u => u.active !== false));
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  // Auto-clear provisional date when confirmed date is set
  useEffect(() => {
    if (confirmedDate && provisionalDate) {
      form.setValue('provisional_install_date', '');
    }
  }, [confirmedDate, provisionalDate, form]);

  // Load customer data for edit mode
  useEffect(() => {
    const loadCustomer = async () => {
      if (isEdit && id) {
        try {
          const response = await customersAPI.getById(id);
          const data = response.data;
          form.reset({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            postcode: data.postcode || '',
            uprn: data.uprn || '',
            mpan: data.mpan || '',
            mpan_lookup_status: data.mpan_lookup_status || '',
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            property_type: data.property_type || 'residential',
            monthly_bill: data.monthly_bill || 0,
            monthly_usage: data.monthly_usage || 0,
            annual_usage: data.annual_usage || 0,
            system_size: data.system_size || 0,
            system_total_cost: data.system_total_cost || 0,
            vat_rate: data.vat_rate || 0,
            roof_type: data.roof_type || 'not_specified',
            roof_orientation: data.roof_orientation || 'not_specified',
            system_type_solar: data.system_type_solar || false,
            system_type_solar_battery: data.system_type_solar_battery || false,
            system_type_electrical: data.system_type_electrical || false,
            system_type_battery_only: data.system_type_battery_only || false,
            system_type_other: data.system_type_other || false,
            system_type_ev_charger: data.system_type_ev_charger || false,
            electrical_description: data.electrical_description || '',
            other_description: data.other_description || '',
            ev_charger_description: data.ev_charger_description || '',
            ev_charger_manufacturer: data.ev_charger_manufacturer || '',
            ev_charger_model: data.ev_charger_model || '',
            ev_charger_quantity: data.ev_charger_quantity || 0,
            panel_manufacturer: data.panel_manufacturer || '',
            panel_model: data.panel_model || '',
            panel_quantity: data.panel_quantity || 0,
            inverter_manufacturer: data.inverter_manufacturer || '',
            inverter_model: data.inverter_model || '',
            inverter_device_ref: data.inverter_device_ref || '',
            inverter_device_data: data.inverter_device_data || null,
            inverter_quantity: data.inverter_quantity || 0,
            gen_meter_model: data.gen_meter_model || '',
            battery_manufacturer: data.battery_manufacturer || '',
            battery_model: data.battery_model || '',
            battery_device_ref: data.battery_device_ref || '',
            battery_device_data: data.battery_device_data || null,
            battery_quantity: data.battery_quantity || 0,
            consumer_unit_manufacturer: data.consumer_unit_manufacturer || '',
            additional_extras: data.additional_extras || '',
            additional_equipment: Array.isArray(data.additional_equipment) ? data.additional_equipment : [],
            cutout_image_url: data.cutout_image_url || '',
            existing_technologies: Array.isArray(data.existing_technologies) && data.existing_technologies.length
              ? data.existing_technologies
              : [{ type: '', type_other: '', action: 'retain', make: '', model: '', size: '', quantity: 1, image_url: '' }],
            provisional_install_date: data.provisional_install_date || '',
            confirmed_install_date: data.confirmed_install_date || '',
            installation_days: data.installation_days || 1,
            notes: data.notes || '',
            status: data.status || 'lead',
            assigned_user_ids: data.assigned_user_ids || [],
            portal_emails_enabled: data.portal_emails_enabled !== false,
            referral_source: data.referral_source || '',
            referred_by_name: data.referred_by_name || '',
            // Carry over pricing / payment split so they're visible & editable on the edit page
            deposit_amount: data.deposit_amount || 0,
            deposit_due_date: data.deposit_due_date || '',
            deposit_paid_date: data.deposit_paid_date || '',
            final_bill_amount: data.final_bill_amount || 0,
            final_bill_due_date: data.final_bill_due_date || '',
            final_bill_paid_date: data.final_bill_paid_date || '',
            on_finance: data.on_finance || false,
            finance_amount: data.finance_amount || 0,
            pay_in_stages: data.pay_in_stages || (data.stage_payments && data.stage_payments.length > 0) || false,
            stage_payments_draft: (data.stage_payments || []).map((sp) => ({
              id: sp.id,
              label: sp.label || '',
              amount: sp.amount || 0,
              due_date: sp.due_date || '',
            })),
          });
          originalCustomerRef.current = {
            ...data,
            confirmed_install_date: data.confirmed_install_date || '',
            provisional_install_date: data.provisional_install_date || '',
          };
        } catch (error) {
          toast.error('Customer not found');
          navigate('/customers');
        } finally {
          setInitialLoading(false);
        }
      }
    };
    
    loadCustomer();
  }, [id, isEdit, navigate, form]);

  // Recompute deposit (25%) / final (75%) off the GROSS (inc-VAT) total.
  const recalcDepositFinal = (vatOverride) => {
    const cost = parseFloat(form.watch('system_total_cost')) || 0;
    if (cost <= 0) return;
    const vat = vatOverride ?? (parseFloat(form.watch('vat_rate')) || 0);
    const gross = cost * (1 + vat / 100);
    const finance = form.watch('on_finance') ? (parseFloat(form.watch('finance_amount')) || 0) : 0;
    const oop = Math.max(gross - finance, 0);
    form.setValue('deposit_amount', Math.round(oop * 0.25 * 100) / 100);
    form.setValue('final_bill_amount', Math.round(oop * 0.75 * 100) / 100);
  };

  // Auto-suggest 20% VAT for commercial/business customers (only if not already set).
  const suggestCommercialVat = () => {
    const current = parseFloat(form.watch('vat_rate')) || 0;
    if (current === 0) {
      form.setValue('vat_rate', 20);
      recalcDepositFinal(20);
      toast.info('VAT set to 20% for a commercial customer — change it if this job should be zero-rated.');
    }
  };


  const onSubmit = async (data) => {
    // EV charger / sub-board prompt: if a battery is being installed (with or
    // without solar) and no sub-board has been added yet, ask about an EV
    // charger before saving. Answered once per form session.
    const hasBattery = data.system_type_solar_battery || data.system_type_battery_only;
    const hasSubBoard = (data.additional_extras || '').toLowerCase().includes('sub board required for ev');
    if (hasBattery && !hasSubBoard && !evAnsweredRef.current) {
      setEvSubBoardOpen(true);
      return;
    }
    // Missing-inverter confirmation: a PV or battery system normally needs an
    // inverter. If none was entered, confirm the omission before saving.
    const inverterRelevant = data.system_type_solar || data.system_type_solar_battery || data.system_type_battery_only;
    const noInverter = !String(data.inverter_manufacturer || '').trim()
      && !String(data.inverter_model || '').trim()
      && !(Number(data.inverter_quantity) > 0);
    if (inverterRelevant && noInverter && !inverterAnsweredRef.current) {
      setInverterPromptOpen(true);
      return;
    }
    // New customer only: let the user name the job/project before it's created
    // (defaults to the first line of the address). Answered once per session.
    if (!isEdit && !jobNamedRef.current) {
      const firstLine = (data.address || '').replace(/\r/g, '').split('\n')[0].split(',')[0].trim();
      setJobNameInput(firstLine);
      setJobNameOpen(true);
      return;
    }
    setLoading(true);
    try {
      // If confirmed date is set, clear provisional date
      if (data.confirmed_install_date && data.provisional_install_date) {
        data.provisional_install_date = null;
      }

      // Detect if a customer-facing install date was newly set or changed —
      // if so we'll bounce the admin to CustomerDetail with a flag that
      // auto-opens the SendEmailDialog with the install-scheduled template.
      const original = originalCustomerRef.current || {};
      const confirmedChanged =
        !!data.confirmed_install_date && data.confirmed_install_date !== (original.confirmed_install_date || '');
      const provisionalChanged =
        !!data.provisional_install_date && data.provisional_install_date !== (original.provisional_install_date || '');
      const shouldPromptEmail = isEdit && (confirmedChanged || provisionalChanged);
      // Detect a customer email change on edit — we'll offer to resend the
      // contract + invoice to the new address after saving.
      const emailChanged = isEdit && !!data.email && data.email !== (original.email || '');

      // Strip the stage-payments draft from the customer payload — it's
      // persisted via the dedicated /stage-payments endpoints below so the
      // existing CustomerUpdate model isn't impacted.
      const stageDraft = (data.stage_payments_draft || []).filter(
        (s) => (s.label && s.label.trim()) || (parseFloat(s.amount) > 0) || s.due_date
      );
      delete data.stage_payments_draft;

      if (isEdit) {
        await customersAPI.update(id, data);
        toast.success('Customer updated successfully');

        // Reconcile stage payments — naive sync: only adds new ones that
        // weren't already on the customer (matched on id). Existing ones
        // can still be edited via the Stage Payments section on the detail
        // page.
        if (data.pay_in_stages && stageDraft.length > 0) {
          for (const sp of stageDraft) {
            if (sp.id) {
              try {
                await api.patch(`/customers/${id}/stage-payments/${sp.id}`, {
                  label: sp.label,
                  amount: parseFloat(sp.amount) || 0,
                  due_date: sp.due_date || null,
                });
              } catch (e) { console.warn('stage update failed', e); }
            } else {
              try {
                await api.post(`/customers/${id}/stage-payments`, {
                  label: sp.label || 'Stage',
                  amount: parseFloat(sp.amount) || 0,
                  due_date: sp.due_date || null,
                });
              } catch (e) { console.warn('stage create failed', e); }
            }
          }
        }

        // If equipment changed and customer already has an agreed snapshot,
        // surface the Variation of Contract dialog before navigating away.
        try {
          const equipmentChanged = ['panel', 'inverter', 'battery', 'ev_charger'].some(k =>
            (original[`${k}_manufacturer`] || '') !== (data[`${k}_manufacturer`] || '') ||
            (original[`${k}_model`] || '') !== (data[`${k}_model`] || '') ||
            (original[`${k}_quantity`] || 0) !== (data[`${k}_quantity`] || 0)
          ) || (original.additional_extras || '') !== (data.additional_extras || '')
            || JSON.stringify(original.additional_equipment || []) !== JSON.stringify(data.additional_equipment || [])
            // Also trigger when the contract total moves (labour rate change,
            // discount applied, etc.) without equipment changing
            || Math.abs((parseFloat(original.system_total_cost) || 0) - (parseFloat(data.system_total_cost) || 0)) > 0.005;

          if (equipmentChanged) {
            const diffRes = await api.get(`/customers/${id}/variation-diff`);
            if (diffRes.data?.has_snapshot && (diffRes.data?.lines || []).length > 0) {
              // Defer navigation until the user dismisses the dialog
              setVariationCustomer({ id, ...data, system_total_cost: diffRes.data.original_contract_sum });
              setVariationDialogOpen(true);
              return;
            }
            // No agreed contract snapshot yet — still prompt the user to keep
            // the paperwork in step: raise a variation or start a new contract.
            setChangeSummary(computeEquipmentChanges(original, data));
            setChangePromptOpen(true);
            return;
          }
        } catch (err) {
          // Non-blocking — fall through to navigate
          console.warn('Variation diff check failed', err);
        }

        if (emailChanged) {
          // Offer to resend the contract + invoice to the new email address.
          setEmailResendNav(shouldPromptEmail ? `/customers/${id}?prompt_email=installation_scheduled` : '/customers');
          setEmailResendOpen(true);
          setLoading(false);
          return;
        }
        if (shouldPromptEmail) {
          navigate(`/customers/${id}?prompt_email=installation_scheduled`);
        } else {
          navigate('/customers');
        }
      } else {
        const createData = { ...data, job_label: (chosenJobNameRef.current || '').trim() };
        const r = await customersAPI.create(createData);
        toast.success('Customer created successfully');
        // If a cut-out image was chosen during creation, attach it to the
        // new customer's documents now (tagged as the cut-out image).
        if (pendingCutout && r.data?.id) {
          try {
            await documentsAPI.upload(r.data.id, pendingCutout, null, 'cutout_image');
            setPendingCutout(null);
          } catch (e) {
            console.warn('Cut-out attach after create failed', e);
            toast.error('Customer saved but the cut-out image could not be added to their files — please re-upload from the customer file.');
          }
        }
        // Persist stage payments on the freshly-created customer
        if (data.pay_in_stages && stageDraft.length > 0 && r.data?.id) {
          for (const sp of stageDraft) {
            try {
              await api.post(`/customers/${r.data.id}/stage-payments`, {
                label: sp.label || 'Stage',
                amount: parseFloat(sp.amount) || 0,
                due_date: sp.due_date || null,
              });
            } catch (e) { console.warn('stage create failed', e); }
          }
        }
        // If a proposal PDF was uploaded during creation, attach it to the
        // new customer now. Sends `pre_extracted` so the backend skips
        // Gemini (already ran once) and just persists the PDF + the
        // `mcs_solar_pv_data` blob + auto-builds the MCS Schedule 1 doc.
        if (pendingProposal?.file && r.data?.id) {
          try {
            const fd = new FormData();
            fd.append('file', pendingProposal.file);
            fd.append('pre_extracted', JSON.stringify(pendingProposal.extracted || {}));
            await runProposalJob(
              api,
              `/dno/extract-from-proposal/start?customer_id=${encodeURIComponent(r.data.id)}`,
              fd,
            );
          } catch (e) {
            console.warn('Proposal attach after create failed', e);
            toast.error('Customer saved but the proposal PDF could not be attached — please re-upload from the customer file.');
          }
        }
        // Open the post-create invoice prompt
        setCreatedCustomer(r.data || data);
        setPostCreateOpen(true);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  };

  // EV sub-board prompt handlers
  const submitAfterEvPrompt = () => {
    setEvSubBoardOpen(false);
    form.handleSubmit(onSubmit)();
  };
  const confirmJobName = () => {
    chosenJobNameRef.current = jobNameInput.trim();
    jobNamedRef.current = true;
    setJobNameOpen(false);
    form.handleSubmit(onSubmit)();
  };
  const handleEvDismiss = () => {
    evAnsweredRef.current = true;
    submitAfterEvPrompt();
  };
  const submitAfterInverterPrompt = () => {
    setInverterPromptOpen(false);
    form.handleSubmit(onSubmit)();
  };
  const handleInverterConfirmSkip = () => {
    inverterAnsweredRef.current = true;
    submitAfterInverterPrompt();
  };
  const handleInverterGoBack = () => {
    setInverterPromptOpen(false);
    // Bring the inverter field into view so it's easy to fill in.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="inverter-manufacturer"]') ||
        document.querySelector('input[name="inverter_manufacturer"]');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
    }, 100);
  };
  const handleEvAddSubBoard = () => {
    evAnsweredRef.current = true;
    const cur = parseFloat(form.getValues('system_total_cost')) || 0;
    form.setValue('system_total_cost', cur + 250);
    const extras = (form.getValues('additional_extras') || '').trim();
    form.setValue(
      'additional_extras',
      extras ? `${extras}\nSub Board required for EV` : 'Sub Board required for EV'
    );
    toast.success('£250 sub-board cost added for EV charger');
    submitAfterEvPrompt();
  };

  // Email-change resend handlers
  const handleResendYes = async () => {
    setEmailResendBusy(true);
    try {
      const { data } = await api.post(`/customers/${id}/resend-key-documents`);
      const bits = [];
      if (data.contract_sent) bits.push('contract');
      if (data.invoice_sent) bits.push('invoice');
      if (bits.length) toast.success(`Sent ${bits.join(' & ')} to the new email address`);
      else toast.info(data.notes?.join('. ') || 'Nothing available to resend');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend documents');
    } finally {
      setEmailResendBusy(false);
      setEmailResendOpen(false);
      navigate(emailResendNav);
    }
  };
  const handleResendNo = () => {
    setEmailResendOpen(false);
    navigate(emailResendNav);
  };

  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="customer-form-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">
            {isEdit ? 'Edit Customer' : 'Add New Customer'}
          </h1>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit, (errors) => {
            const first = Object.values(errors || {})[0];
            const msg = first?.message || 'Please complete the required equipment details before saving.';
            toast.error(typeof msg === 'string' ? msg : 'Please complete the required fields before saving.');
          })} className="space-y-6">
            {/* Solar PV Proposal upload — auto-fills the form below.
                Shown on both Create and Edit. For edit, attaches immediately
                to the existing customer; for create, holds the PDF until
                save then attaches in onSubmit. */}
            <ProposalUploadCard
              ref={proposalCardRef}
              form={form}
              customerId={isEdit ? id : null}
              onProposalReady={({ file, extracted }) => {
                if (!isEdit) setPendingProposal({ file, extracted });
              }}
            />

            {/* Create-from-Xero-quote auto-fill — only on the new-customer flow. */}
            {!isEdit && <XeroQuoteImportCard form={form} />}

            {/* Personal Information */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} data-testid="first-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Smith" {...field} data-testid="last-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john@example.com" {...field} data-testid="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone *</FormLabel>
                      <FormControl>
                        <Input placeholder="0412 345 678" {...field} data-testid="phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="portal_emails_enabled"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2 flex flex-row items-start gap-3 rounded-lg border border-slate-200 p-4 bg-slate-50">
                      <FormControl>
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600"
                          data-testid="portal-emails-enabled"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-tight">
                        <FormLabel className="text-sm font-medium cursor-pointer">
                          Send Customer Portal sign-up email & status updates
                        </FormLabel>
                        <p className="text-xs text-slate-500">
                          When enabled, the customer receives a portal welcome email on creation and automatic status-change emails (quote, deposit, install, completion). Untick if this customer should not be contacted via the portal — handy for trade clients, internal records, or customers who've requested no automated emails.
                        </p>
                      </div>
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Address
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Street Address *</FormLabel>
                      <FormControl>
                        <AddressAutocomplete
                          value={field.value || ''}
                          onChange={field.onChange}
                          onSelect={(s) => {
                            form.setValue('address', s.address_line_1 || s.label);
                            if (s.city) form.setValue('city', s.city);
                            if (s.county) form.setValue('state', s.county);
                            if (s.postcode) form.setValue('postcode', s.postcode);
                            form.setValue('uprn', s.uprn || '');
                            if (s.mpan) {
                              form.setValue('mpan', s.mpan);
                              form.setValue('mpan_lookup_status', 'found');
                            } else {
                              // Address picked but no MPAN attached — try a
                              // dedicated ENA MPAN lookup, flag on failure.
                              form.setValue('mpan', '');
                              lookupMpan({ silent: true });
                            }
                          }}
                          placeholder="123 Solar Street — start typing to search"
                          testId="address"
                        />
                      </FormControl>
                      <FormDescription className="text-xs">
                        Type a street/postcode to search (ENA), or use the postcode "Find" button below.
                      </FormDescription>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        {form.watch('uprn') && (
                          <Badge variant="outline" className="text-xs font-mono text-emerald-700 border-emerald-300 bg-emerald-50" data-testid="customer-uprn-badge">
                            UPRN: {form.watch('uprn')}
                          </Badge>
                        )}
                      </div>
                      {/* MPAN — always editable so staff can enter/correct it. */}
                      <div className="mt-3 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <label className="text-xs font-medium text-slate-600">MPAN (electricity supply number)</label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={mpanLooking}
                            onClick={() => lookupMpan()}
                            className="h-7 px-2 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                            data-testid="mpan-lookup-btn"
                          >
                            {mpanLooking ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
                            {form.watch('mpan') ? 'Re-check via ENA' : 'Look up via ENA'}
                          </Button>
                        </div>
                        <Input
                          placeholder="e.g. 20 0001 2345 678 (13 or 21 digits)"
                          value={form.watch('mpan') || ''}
                          onChange={(e) => {
                            form.setValue('mpan', e.target.value);
                            form.setValue('mpan_lookup_status', e.target.value ? 'manual' : '');
                          }}
                          className="h-9 font-mono"
                          data-testid="mpan-manual-input"
                        />
                        {!form.watch('mpan') && form.watch('mpan_lookup_status') === 'failed' && (
                          <p className="text-xs text-amber-700 flex items-center gap-1" data-testid="mpan-failed-hint">
                            <AlertCircle className="w-3.5 h-3.5" /> ENA couldn't find an MPAN for this address — please enter it manually above.
                          </p>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City/Town</FormLabel>
                      <FormControl>
                        <Input placeholder="London" {...field} data-testid="city" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>County</FormLabel>
                        <FormControl>
                          <Input placeholder="Kent" {...field} data-testid="state" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="postcode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postcode</FormLabel>
                        <div className="flex gap-2">
                          <FormControl>
                            <Input placeholder="SW1A 1AA" {...field} data-testid="postcode"
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePostcodeLookup(); } }}
                            />
                          </FormControl>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={postcodeLooking}
                            onClick={handlePostcodeLookup}
                            className="shrink-0 h-10 px-3 text-blue-600 border-blue-300 hover:bg-blue-50"
                            data-testid="find-address-btn"
                          >
                            {postcodeLooking ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Search className="w-4 h-4 mr-1" />
                            )}
                            Find
                          </Button>
                        </div>
                        {postcodeResult && (
                          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-1" data-testid="postcode-result">
                            <p className="font-medium text-blue-800">{postcodeResult.postcode}</p>
                            {postcodeResult.ward && <p className="text-blue-700">Ward: {postcodeResult.ward}</p>}
                            {postcodeResult.parish && <p className="text-blue-700">Parish: {postcodeResult.parish}</p>}
                            <p className="text-blue-700">{[postcodeResult.city, postcodeResult.county].filter(Boolean).join(', ')}</p>
                            {postcodeResult.distance_from_hq_miles != null && (
                              <p className="text-blue-600 text-xs">{postcodeResult.distance_from_hq_miles} miles from HQ</p>
                            )}
                            <p className="text-blue-500 text-xs mt-1">Enter the house number/name in the Address field above</p>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Property Details */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Property Details
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <FormField
                  control={form.control}
                  name="property_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Property Type</FormLabel>
                      <Select onValueChange={(val) => { field.onChange(val); if (val === 'commercial' || val === 'industrial') suggestCommercialVat(); }} value={field.value || 'residential'}>
                        <FormControl>
                          <SelectTrigger data-testid="property-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PROPERTY_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="roof_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Roof Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'not_specified'}>
                        <FormControl>
                          <SelectTrigger data-testid="roof-type">
                            <SelectValue placeholder="Select roof type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not specified</SelectItem>
                          {ROOF_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="roof_orientation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Roof Orientation</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'not_specified'}>
                        <FormControl>
                          <SelectTrigger data-testid="roof-orientation">
                            <SelectValue placeholder="Select orientation" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not specified</SelectItem>
                          {ROOF_ORIENTATIONS.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Energy & System */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Energy & System Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* System Type Radio Buttons (single select) */}
                <div className="space-y-3">
                  <FormLabel>System Type</FormLabel>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { name: 'system_type_solar_battery', label: 'Solar & Battery', testId: 'system-type-solar-battery' },
                      { name: 'system_type_battery_only', label: 'Battery Only', testId: 'system-type-battery-only' },
                      { name: 'system_type_solar', label: 'Solar', testId: 'system-type-solar' },
                      { name: 'system_type_ev_charger', label: 'EV Charger', testId: 'system-type-ev-charger' },
                      { name: 'system_type_electrical', label: 'Electrical', testId: 'system-type-electrical' },
                      { name: 'system_type_other', label: 'Other', testId: 'system-type-other' },
                    ].map(({ name, label, testId }) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2 space-y-0">
                            <FormControl>
                              <div
                                className={`w-4 h-4 rounded-full border-2 cursor-pointer flex items-center justify-center transition-colors ${
                                  field.value
                                    ? 'border-blue-600 bg-blue-600'
                                    : 'border-slate-300 dark:border-slate-600 hover:border-blue-400'
                                }`}
                                onClick={() => {
                                  // Radio behavior: deselect all others, toggle this one
                                  const allTypes = [
                                    'system_type_solar_battery', 'system_type_battery_only',
                                    'system_type_solar', 'system_type_ev_charger',
                                    'system_type_electrical', 'system_type_other'
                                  ];
                                  allTypes.forEach(t => form.setValue(t, t === name ? !field.value : false));
                                }}
                                data-testid={testId}
                              >
                                {field.value && (
                                  <div className="w-2 h-2 rounded-full bg-white" />
                                )}
                              </div>
                            </FormControl>
                            <FormLabel
                              className="font-normal cursor-pointer"
                              onClick={() => {
                                const allTypes = [
                                  'system_type_solar_battery', 'system_type_battery_only',
                                  'system_type_solar', 'system_type_ev_charger',
                                  'system_type_electrical', 'system_type_other'
                                ];
                                allTypes.forEach(t => form.setValue(t, t === name ? !field.value : false));
                              }}
                            >{label}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>

                {/* Energy metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <FormField
                    control={form.control}
                    name="monthly_bill"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Monthly Bill (£)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.01" 
                            placeholder="250.00" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            data-testid="monthly-bill" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="monthly_usage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Monthly Usage (kWh)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.1" 
                            placeholder="500" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            data-testid="monthly-usage" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="annual_usage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Annual Usage (kWh)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.1" 
                            placeholder="3600" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            data-testid="annual-usage" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="system_size"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>System Size (kW)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            step="0.1" 
                            placeholder="6.6" 
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
                            data-testid="system-size" 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Equipment Manufacturers with Quantities */}
                <div className="space-y-4">
                  {/* Solar Panels - shown for Solar, Solar & Battery, or no type selected */}
                  {(showSolarPanel || showAllEquipment) && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="panel_manufacturer"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Solar Panel Manufacturer</FormLabel>
                            <FormControl>
                                <ProductInput value={field.value} onChange={field.onChange} options={catManufacturers('panel')} placeholder="e.g., LG, Jinko, Trina" testId="panel-manufacturer" nudgeLabel="Manufacturer not in catalog — please double-check the name." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="panel_model"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Model</FormLabel>
                          <FormControl>
                            <ProductInput value={field.value} onChange={field.onChange} options={catModels('panel', form.watch('panel_manufacturer'))} placeholder="e.g., NeON R 380W" testId="panel-model" nudgeLabel="Model not in catalog — please double-check the model number." />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="panel_quantity"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Quantity</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" {...field} data-testid="panel-quantity" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="md:col-span-4">
                      <AdditionalEquipmentLines form={form} category="panel" label="Solar Panel" catManufacturers={catManufacturers} catModels={catModels} />
                    </div>

                  </div>
                  )}

                  {/* Inverter - shown for Solar, Solar & Battery, or no type selected */}
                  {(showInverter || showAllEquipment) && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2">
                        <FormField
                          control={form.control}
                          name="inverter_manufacturer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Inverter Manufacturer</FormLabel>
                              <FormControl>
                                <ProductInput value={field.value} onChange={field.onChange} options={catManufacturers('inverter')} placeholder="e.g., Fox ESS, GivEnergy, SolarEdge" testId="inverter-manufacturer" nudgeLabel="Manufacturer not in catalog — please double-check the name." />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="inverter_model"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Model</FormLabel>
                            <FormControl>
                              <ProductInput value={field.value} onChange={field.onChange} options={catModels('inverter', form.watch('inverter_manufacturer'))} placeholder="e.g., SUN-8K-SG04LP3" testId="inverter-model" nudgeLabel="Model not in catalog — please double-check the model number." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="inverter_quantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} data-testid="inverter-quantity" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="gen_meter_model"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Generation meter</FormLabel>
                            <FormControl>
                              <GenMeterSelect value={field.value} onChange={field.onChange}
                                inverterManufacturer={form.watch('inverter_manufacturer')} testId="gen-meter-model" />
                            </FormControl>
                            <FormDescription className="text-xs">Printed under the M symbol on the SLD. Auto: Solis / SunSynk → Eastron, otherwise Emlite ECA2A-NZ.</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    {/* TTR Device Search */}
                    <div>
                      <FormLabel className="text-xs text-blue-700">ENA Type Test Register Reference</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between mt-1 border-blue-300 hover:bg-blue-100"
                        onClick={() => setTtrDialogTarget('inverter')}
                        data-testid="inverter-ttr-search-btn"
                      >
                        {form.getValues('inverter_device_ref') ? (
                          <span className="text-left truncate text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-600 inline mr-1" />
                            <span className="font-mono">{form.getValues('inverter_device_ref')}</span>
                            {form.getValues('inverter_device_data')?.ModelNo && (
                              <span className="text-slate-500 ml-2">— {form.getValues('inverter_device_data')?.TTRManufacturer} {form.getValues('inverter_device_data')?.ModelNo}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-blue-500 text-sm">Search ENA Type Test Register...</span>
                        )}
                        <Search className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      </Button>
                    </div>
                    <AdditionalEquipmentLines form={form} category="inverter" label="Inverter" catManufacturers={catManufacturers} catModels={catModels} />

                  </div>
                  )}

                  {/* Battery - shown for Solar & Battery, Battery Only, or no type selected */}
                  {(showBattery || showAllEquipment) && (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2">
                        <FormField
                          control={form.control}
                          name="battery_manufacturer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Battery Manufacturer</FormLabel>
                              <FormControl>
                                <ProductInput value={field.value} onChange={field.onChange} options={catManufacturers('battery')} placeholder="e.g., Tesla, BYD, Fox ESS" testId="battery-manufacturer" nudgeLabel="Manufacturer not in catalog — please double-check the name." />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="battery_model"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Model</FormLabel>
                            <FormControl>
                              <ProductInput value={field.value} onChange={field.onChange} options={catModels('battery', form.watch('battery_manufacturer'))} placeholder="e.g., Powerwall 2, HV2600" testId="battery-model" nudgeLabel="Model not in catalog — please double-check the model number." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="battery_quantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} data-testid="battery-quantity" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    {/* TTR Device Search */}
                    <div>
                      <FormLabel className="text-xs text-emerald-700">ENA Type Test Register Reference</FormLabel>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-between mt-1 border-emerald-300 hover:bg-emerald-100"
                        onClick={() => setTtrDialogTarget('battery')}
                        data-testid="battery-ttr-search-btn"
                      >
                        {form.getValues('battery_device_ref') ? (
                          <span className="text-left truncate text-sm">
                            <CheckCircle className="w-4 h-4 text-emerald-600 inline mr-1" />
                            <span className="font-mono">{form.getValues('battery_device_ref')}</span>
                            {form.getValues('battery_device_data')?.ModelNo && (
                              <span className="text-slate-500 ml-2">— {form.getValues('battery_device_data')?.TTRManufacturer} {form.getValues('battery_device_data')?.ModelNo}</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-emerald-500 text-sm">Search ENA Type Test Register...</span>
                        )}
                        <Search className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      </Button>
                    </div>
                    <AdditionalEquipmentLines form={form} category="battery" label="Battery" catManufacturers={catManufacturers} catModels={catModels} />

                  </div>
                  )}

                  {/* Electrical free text - shown for Electrical type */}
                  {showElectricalFreeText && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <FormField
                      control={form.control}
                      name="electrical_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Electrical Work Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the electrical work required..."
                              rows={4}
                              {...field}
                              data-testid="electrical-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  )}

                  {/* Other free text - shown for Other type */}
                  {showOtherFreeText && (
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                    <FormField
                      control={form.control}
                      name="other_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Other Work Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the work required..."
                              rows={4}
                              {...field}
                              data-testid="other-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  )}

                  {/* EV Charger fields - shown for EV Charger type */}
                  {showEvCharger && (
                  <div className="space-y-4 p-4 bg-teal-50 dark:bg-teal-950/30 rounded-lg border border-teal-200 dark:border-teal-800">
                    <FormField
                      control={form.control}
                      name="ev_charger_description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>EV Charger Description</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Describe the EV charger installation requirements..."
                              rows={3}
                              {...field}
                              data-testid="ev-charger-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="md:col-span-2">
                        <FormField
                          control={form.control}
                          name="ev_charger_manufacturer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>EV Charger Manufacturer</FormLabel>
                              <FormControl>
                                <ProductInput value={field.value} onChange={field.onChange} options={catManufacturers('ev_charger')} placeholder="e.g., Zappi, Pod Point, Ohme" testId="ev-charger-manufacturer" nudgeLabel="Manufacturer not in catalog — please double-check the name." />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="ev_charger_model"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Model</FormLabel>
                            <FormControl>
                              <ProductInput value={field.value} onChange={field.onChange} options={catModels('ev_charger', form.watch('ev_charger_manufacturer'))} placeholder="e.g., Zappi v2.1, Solo 3" testId="ev-charger-model" nudgeLabel="Model not in catalog — please double-check the model number." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="ev_charger_quantity"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Quantity</FormLabel>
                            <FormControl>
                              <Input type="number" min="0" {...field} data-testid="ev-charger-quantity" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <AdditionalEquipmentLines form={form} category="ev_charger" label="EV Charger" catManufacturers={catManufacturers} catModels={catModels} />

                  </div>
                  )}

                  {/* Consumer Unit */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="consumer_unit_manufacturer"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Consumer Unit Manufacturer</FormLabel>
                            <FormControl>
                              <ProductInput value={field.value} onChange={field.onChange} options={catManufacturers('consumer_unit')} placeholder="e.g., Hager, MK, Schneider" testId="consumer-unit-manufacturer" nudgeLabel="Manufacturer not in catalog — please double-check the name." />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Additional Extras — shown to customer in their portal */}
                  <FormField
                    control={form.control}
                    name="additional_extras"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Additional Extras</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder={'Anything extra included in this install — one per line. For example:\n• Scaffolding\n• Bird mesh\n• DC isolator upgrade\n• EV charger commissioning visit'}
                            rows={4}
                            {...field}
                            data-testid="additional-extras"
                          />
                        </FormControl>
                        <p className="text-xs text-slate-500 mt-1">
                          These extras appear on the customer&apos;s portal so they know exactly what&apos;s included.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Cut-Out Image for DNO Applications */}
            <ExistingTechnologiesSection form={form} />

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  DNO Application — Cut-Out Image
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">Upload a photo of the property's electrical cut-out point. It's saved to the customer's files, backed up to SharePoint, marked as the cut-out image, and sent to ENA for the DNO application.</p>
                <div className="flex items-center gap-4">
                  <input
                    type="file"
                    accept="image/*"
                    className="text-sm"
                    data-testid="cutout-image-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingCutout(true);
                      let savedToFile = false;
                      // 1) Save to the customer's files (documents), back up to
                      //    SharePoint and mark as the cut-out image. Needs a saved
                      //    customer (edit mode).
                      if (isEdit && id) {
                        try {
                          await documentsAPI.upload(id, file, null, 'cutout_image');
                          savedToFile = true;
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to save cut-out image to customer files');
                        }
                      } else {
                        // New customer: hold the file and attach it to the
                        // customer's documents right after they're created.
                        setPendingCutout(file);
                      }
                      // 2) Upload to ENA for the DNO application (best-effort).
                      try {
                        const res = await dnoAPI.uploadAttachment(file, 'image');
                        const url = res.data?.file_url || '';
                        if (url) form.setValue('cutout_image_url', url);
                      } catch (err) {
                        if (!savedToFile) {
                          toast.error(err.response?.data?.detail || 'Failed to upload cut-out image');
                        }
                      } finally {
                        setUploadingCutout(false);
                      }
                      if (savedToFile) {
                        toast.success('Cut-out image saved to customer files, backed up to SharePoint & marked as the cut-out');
                      } else if (!isEdit) {
                        toast.success('Cut-out image ready — it will be saved to the customer\'s files when you save the customer');
                      }
                    }}
                  />
                  {uploadingCutout && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
                  {form.watch('cutout_image_url') && (
                    <span className="flex items-center gap-1 text-sm text-emerald-700">
                      <CheckCircle className="w-4 h-4" /> Uploaded
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Commercial Client Details */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Commercial Client Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="is_commercial"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Commercial Client</FormLabel>
                        <FormDescription>
                          Enable this for business/commercial customers
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(v) => { field.onChange(v); if (v) suggestCommercialVat(); }}
                          data-testid="is-commercial"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                {form.watch('is_commercial') && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border">
                    <FormField
                      control={form.control}
                      name="company_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Company Ltd" {...field} data-testid="company-name" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="company_registration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Company Registration No.</FormLabel>
                          <FormControl>
                            <Input placeholder="12345678" {...field} data-testid="company-registration" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vat_number"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>VAT Number</FormLabel>
                          <FormControl>
                            <Input placeholder="GB123456789" {...field} data-testid="vat-number" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Installation Dates */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Installation Schedule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {confirmedDate && provisionalDate && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      Setting a confirmed date will clear the provisional date.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="provisional_install_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provisional Installation Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            value={field.value || ''}
                            disabled={!!confirmedDate}
                            className={confirmedDate ? 'bg-slate-100' : ''}
                            data-testid="provisional-date"
                          />
                        </FormControl>
                        <FormDescription>
                          Tentative date before confirmation
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmed_install_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmed Installation Date</FormLabel>
                        <FormControl>
                          <Input 
                            type="date" 
                            {...field}
                            value={field.value || ''}
                            data-testid="confirmed-date"
                          />
                        </FormControl>
                        <FormDescription>
                          Final confirmed date (clears provisional)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <FormField
                    control={form.control}
                    name="installation_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Installation Duration (Days)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            min="1"
                            {...field}
                            value={field.value || 1}
                            data-testid="installation-days"
                          />
                        </FormControl>
                        <FormDescription>
                          Number of days the installation will take
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payments */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Referral Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="referral_source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>How did they hear about us?</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || ''}>
                          <FormControl>
                            <SelectTrigger data-testid="referral-source">
                              <SelectValue placeholder="Select source" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="word_of_mouth">Word of Mouth</SelectItem>
                            <SelectItem value="google">Google Search</SelectItem>
                            <SelectItem value="facebook">Facebook / Instagram</SelectItem>
                            <SelectItem value="website">Website</SelectItem>
                            <SelectItem value="flyer">Flyer / Leaflet</SelectItem>
                            <SelectItem value="referral">Customer Referral</SelectItem>
                            <SelectItem value="trade_show">Trade Show / Event</SelectItem>
                            <SelectItem value="yell">Yell / Directory</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="referred_by_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Referred by (name)</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. John Smith" {...field} data-testid="referred-by-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Payments */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <PoundSterling className="w-5 h-5" />
                  Payments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* System Price (ex VAT) + VAT selector */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="system_total_cost"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>System Price (ex VAT) (£)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="15000.00"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => {
                              const cost = e.target.value === '' ? 0 : parseFloat(e.target.value);
                              field.onChange(cost);
                              // Deposit (25%) / Final (75%) of the out-of-pocket
                              // portion of the GROSS (inc-VAT) total, minus finance.
                              if (cost > 0) {
                                const vat = parseFloat(form.watch('vat_rate')) || 0;
                                const gross = cost * (1 + vat / 100);
                                const finance = form.watch('on_finance') ? (parseFloat(form.watch('finance_amount')) || 0) : 0;
                                const outOfPocket = Math.max(gross - finance, 0);
                                form.setValue('deposit_amount', Math.round(outOfPocket * 0.25 * 100) / 100);
                                form.setValue('final_bill_amount', Math.round(outOfPocket * 0.75 * 100) / 100);
                              }
                            }}
                            data-testid="system-total-cost"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="vat_rate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VAT</FormLabel>
                        <Select
                          value={String(field.value ?? 0)}
                          onValueChange={(val) => {
                            const vat = parseFloat(val) || 0;
                            field.onChange(vat);
                            const cost = parseFloat(form.watch('system_total_cost')) || 0;
                            if (cost > 0) {
                              const gross = cost * (1 + vat / 100);
                              const finance = form.watch('on_finance') ? (parseFloat(form.watch('finance_amount')) || 0) : 0;
                              const outOfPocket = Math.max(gross - finance, 0);
                              form.setValue('deposit_amount', Math.round(outOfPocket * 0.25 * 100) / 100);
                              form.setValue('final_bill_amount', Math.round(outOfPocket * 0.75 * 100) / 100);
                            }
                          }}
                        >
                          <FormControl>
                            <SelectTrigger data-testid="vat-rate-select">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0" data-testid="vat-0">0% (domestic)</SelectItem>
                            <SelectItem value="20" data-testid="vat-20">20% (commercial)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-end">
                    <p className="text-xs text-slate-500 pb-2">
                      {form.watch('on_finance')
                        ? 'Deposit / Final = 25% / 75% of (inc-VAT total − finance)'
                        : 'Deposit: 25% / Final: 75% of inc-VAT total'}
                      {currentUser?.is_admin ? ' — admin can override below' : ''}
                    </p>
                  </div>
                </div>

                {/* VAT note + inc-VAT summary */}
                <div className="-mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="vat-note">
                  <p className="text-xs text-amber-800">
                    This is an <strong>ex-VAT</strong> price — select VAT at <strong>20%</strong> if a commercial customer.
                  </p>
                  {(parseFloat(form.watch('system_total_cost')) || 0) > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-amber-900" data-testid="vat-summary">
                      <span>Subtotal (ex VAT): <strong>£{(parseFloat(form.watch('system_total_cost')) || 0).toFixed(2)}</strong></span>
                      <span>VAT ({parseFloat(form.watch('vat_rate')) || 0}%): <strong>£{((parseFloat(form.watch('system_total_cost')) || 0) * ((parseFloat(form.watch('vat_rate')) || 0) / 100)).toFixed(2)}</strong></span>
                      <span>Total (inc VAT): <strong>£{((parseFloat(form.watch('system_total_cost')) || 0) * (1 + (parseFloat(form.watch('vat_rate')) || 0) / 100)).toFixed(2)}</strong></span>
                    </div>
                  )}
                </div>

                {/* Finance toggle */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <FormField
                    control={form.control}
                    name="on_finance"
                    render={({ field }) => (
                      <FormItem>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={(e) => {
                              const isOn = e.target.checked;
                              field.onChange(isOn);
                              const cost = parseFloat(form.watch('system_total_cost')) || 0;
                              const vat = parseFloat(form.watch('vat_rate')) || 0;
                              const gross = cost * (1 + vat / 100);
                              const finance = isOn ? (parseFloat(form.watch('finance_amount')) || 0) : 0;
                              if (!isOn) {
                                form.setValue('finance_amount', 0);
                              }
                              const outOfPocket = Math.max(gross - finance, 0);
                              if (cost > 0) {
                                form.setValue('deposit_amount', Math.round(outOfPocket * 0.25 * 100) / 100);
                                form.setValue('final_bill_amount', Math.round(outOfPocket * 0.75 * 100) / 100);
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-purple-600"
                            data-testid="on-finance-toggle"
                          />
                          <span className="text-sm text-purple-900 font-medium">Customer is paying part of this on finance</span>
                        </label>
                      </FormItem>
                    )}
                  />
                  {form.watch('on_finance') && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                      <FormField
                        control={form.control}
                        name="finance_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Finance Amount (£)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="6000.00"
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => {
                                  const finance = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                  field.onChange(finance);
                                  const cost = parseFloat(form.watch('system_total_cost')) || 0;
                                  const vat = parseFloat(form.watch('vat_rate')) || 0;
                                  const gross = cost * (1 + vat / 100);
                                  const outOfPocket = Math.max(gross - finance, 0);
                                  form.setValue('deposit_amount', Math.round(outOfPocket * 0.25 * 100) / 100);
                                  form.setValue('final_bill_amount', Math.round(outOfPocket * 0.75 * 100) / 100);
                                }}
                                data-testid="finance-amount"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="md:col-span-2 flex items-end">
                        <p className="text-xs text-purple-700 pb-2">
                          Out-of-pocket (inc VAT): <strong>£{Math.max(((parseFloat(form.watch('system_total_cost')) || 0) * (1 + (parseFloat(form.watch('vat_rate')) || 0) / 100)) - (parseFloat(form.watch('finance_amount')) || 0), 0).toFixed(2)}</strong>
                          {' '}• Deposit (25%): <strong>£{(Math.max(((parseFloat(form.watch('system_total_cost')) || 0) * (1 + (parseFloat(form.watch('vat_rate')) || 0) / 100)) - (parseFloat(form.watch('finance_amount')) || 0), 0) * 0.25).toFixed(2)}</strong>
                          {' '}• Final (75%): <strong>£{(Math.max(((parseFloat(form.watch('system_total_cost')) || 0) * (1 + (parseFloat(form.watch('vat_rate')) || 0) / 100)) - (parseFloat(form.watch('finance_amount')) || 0), 0) * 0.75).toFixed(2)}</strong>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stage payments toggle — mirror of finance toggle */}
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <FormField
                    control={form.control}
                    name="pay_in_stages"
                    render={({ field }) => (
                      <FormItem>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={(e) => {
                              const isOn = e.target.checked;
                              field.onChange(isOn);
                              if (isOn) {
                                const existing = form.getValues('stage_payments_draft') || [];
                                if (existing.length === 0) {
                                  form.setValue('stage_payments_draft', [
                                    { label: 'Stage 1', amount: 0, due_date: '' },
                                  ], { shouldDirty: true });
                                }
                              }
                            }}
                            className="w-4 h-4 rounded border-slate-300 text-emerald-600"
                            data-testid="pay-in-stages-toggle"
                          />
                          <span className="text-sm text-emerald-900 font-medium">Customer is paying in stages (1–3 stage payments)</span>
                        </label>
                      </FormItem>
                    )}
                  />
                  {form.watch('pay_in_stages') && (
                    <div className="mt-3 space-y-2" data-testid="stage-payments-draft">
                      {(form.watch('stage_payments_draft') || []).map((stage, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded border border-emerald-100 bg-white">
                          <div className="col-span-12 md:col-span-4">
                            <label className="text-[10px] uppercase tracking-wide text-emerald-700">Stage {idx + 1} label</label>
                            <Input
                              value={stage.label}
                              onChange={(e) => {
                                const draft = [...(form.getValues('stage_payments_draft') || [])];
                                draft[idx] = { ...draft[idx], label: e.target.value };
                                form.setValue('stage_payments_draft', draft, { shouldDirty: true });
                              }}
                              placeholder={`Stage ${idx + 1}`}
                              data-testid={`stage-label-${idx}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-3">
                            <label className="text-[10px] uppercase tracking-wide text-emerald-700">Amount (£)</label>
                            <Input
                              type="number"
                              step="0.01"
                              value={stage.amount || ''}
                              onChange={(e) => {
                                const draft = [...(form.getValues('stage_payments_draft') || [])];
                                draft[idx] = { ...draft[idx], amount: parseFloat(e.target.value) || 0 };
                                form.setValue('stage_payments_draft', draft, { shouldDirty: true });
                              }}
                              placeholder="0.00"
                              data-testid={`stage-amount-${idx}`}
                            />
                          </div>
                          <div className="col-span-6 md:col-span-4">
                            <label className="text-[10px] uppercase tracking-wide text-emerald-700">Due date</label>
                            <Input
                              type="date"
                              value={stage.due_date || ''}
                              onChange={(e) => {
                                const draft = [...(form.getValues('stage_payments_draft') || [])];
                                draft[idx] = { ...draft[idx], due_date: e.target.value };
                                form.setValue('stage_payments_draft', draft, { shouldDirty: true });
                              }}
                              data-testid={`stage-due-${idx}`}
                            />
                          </div>
                          <div className="col-span-12 md:col-span-1 flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-rose-600 h-8"
                              onClick={() => {
                                const draft = [...(form.getValues('stage_payments_draft') || [])];
                                draft.splice(idx, 1);
                                form.setValue('stage_payments_draft', draft, { shouldDirty: true });
                              }}
                              data-testid={`stage-remove-${idx}`}
                            >
                              ✕
                            </Button>
                          </div>
                        </div>
                      ))}
                      {(form.watch('stage_payments_draft') || []).length < 3 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => {
                            const draft = [...(form.getValues('stage_payments_draft') || [])];
                            draft.push({ label: `Stage ${draft.length + 1}`, amount: 0, due_date: '' });
                            form.setValue('stage_payments_draft', draft, { shouldDirty: true });
                          }}
                          data-testid="stage-add-btn"
                        >
                          + Add Stage ({(form.watch('stage_payments_draft') || []).length}/3)
                        </Button>
                      )}
                      <p className="text-[11px] text-emerald-700 mt-1">
                        Sum: <strong>£{((form.watch('stage_payments_draft') || []).reduce((acc, s) => acc + (parseFloat(s.amount) || 0), 0)).toFixed(2)}</strong>
                        {' '}— these will be saved as interim invoices alongside the deposit and final bill.
                      </p>
                    </div>
                  )}
                </div>


                {/* Deposit Section */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-3 flex items-center gap-2">
                    Deposit
                    {form.watch('deposit_paid_date') && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Paid
                      </span>
                    )}
                  </h4>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.watch('deposit_paid_date')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setValue('deposit_paid_date', new Date().toISOString().slice(0, 10), { shouldDirty: true });
                        } else {
                          form.setValue('deposit_paid_date', '', { shouldDirty: true });
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600"
                      data-testid="deposit-paid-toggle"
                    />
                    <span className="text-sm text-blue-900 font-medium">Deposit has been paid</span>
                    <span className="text-xs text-slate-500">(ticking sets today's date — adjust below if needed)</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="deposit_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Amount (£)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              step="0.01"
                              min="0"
                              {...field}
                              readOnly={!currentUser?.is_admin}
                              className={!currentUser?.is_admin ? 'bg-slate-50' : ''}
                              data-testid="deposit-amount"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="deposit_due_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value || ''}
                              data-testid="deposit-due-date"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="deposit_paid_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Paid Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value || ''}
                              data-testid="deposit-paid-date"
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Leave empty if not paid
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Final Bill Section */}
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-amber-900 mb-3 flex items-center gap-2">
                    Final Bill
                    {form.watch('final_bill_paid_date') && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Paid
                      </span>
                    )}
                  </h4>
                  <label className="flex items-center gap-2 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.watch('final_bill_paid_date')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          form.setValue('final_bill_paid_date', new Date().toISOString().slice(0, 10), { shouldDirty: true });
                        } else {
                          form.setValue('final_bill_paid_date', '', { shouldDirty: true });
                        }
                      }}
                      className="w-4 h-4 rounded border-slate-300 text-amber-600"
                      data-testid="final-paid-toggle"
                    />
                    <span className="text-sm text-amber-900 font-medium">Final payment has been paid</span>
                    <span className="text-xs text-slate-500">(ticking sets today's date — adjust below if needed)</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name="final_bill_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Amount (£)</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              step="0.01"
                              min="0"
                              {...field}
                              readOnly={!currentUser?.is_admin}
                              className={!currentUser?.is_admin ? 'bg-slate-50' : ''}
                              data-testid="final-bill-amount"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="final_bill_due_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Due Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value || ''}
                              data-testid="final-bill-due-date"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="final_bill_paid_date"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Paid Date</FormLabel>
                          <FormControl>
                            <Input 
                              type="date" 
                              {...field}
                              value={field.value || ''}
                              data-testid="final-bill-paid-date"
                            />
                          </FormControl>
                          <FormDescription className="text-xs">
                            Leave empty if not paid
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assigned Users */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Assigned Team Members
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="assigned_user_ids"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Assign Users</FormLabel>
                      <FormDescription>
                        Select team members to assign to this customer
                      </FormDescription>
                      
                      {/* Selected users display */}
                      {field.value && field.value.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border">
                          {field.value.map((userId) => {
                            const user = availableUsers.find(u => u.id === userId);
                            return user ? (
                              <div
                                key={userId}
                                className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border text-sm"
                              >
                                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-medium">
                                  {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                                </div>
                                <span>{user.name || user.email}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange(field.value.filter(id => id !== userId));
                                  }}
                                  className="text-slate-400 hover:text-red-500 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : null;
                          })}
                        </div>
                      )}
                      
                      {/* User selection */}
                      <div className="border rounded-lg p-3 space-y-2 max-h-[200px] overflow-y-auto">
                        {availableUsers.length > 0 ? (
                          availableUsers.map((user) => (
                            <label
                              key={user.id}
                              className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors"
                            >
                              <Checkbox
                                checked={field.value?.includes(user.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([...(field.value || []), user.id]);
                                  } else {
                                    field.onChange((field.value || []).filter(id => id !== user.id));
                                  }
                                }}
                                data-testid={`assign-user-${user.id}`}
                              />
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-sm font-medium">
                                {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{user.name || 'Unnamed'}</p>
                                <p className="text-xs text-slate-500">{user.email}</p>
                              </div>
                              {user.is_admin && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                  Admin
                                </span>
                              )}
                              {user.is_engineer && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                  Engineer
                                </span>
                              )}
                            </label>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 text-center py-4">
                            No users available
                          </p>
                        )}
                      </div>
                      {field.value && field.value.length > 0 && (
                        <p className="text-xs text-slate-500">
                          {field.value.length} user(s) assigned
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Status & Notes */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Status & Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem className="max-w-xs">
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'lead'}>
                        <FormControl>
                          <SelectTrigger data-testid="status">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PROJECT_STAGES.map((stage) => (
                            <SelectItem key={stage} value={stage}>
                              {STATUS_DISPLAY_NAMES[stage]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Add any additional notes about this customer..."
                          className="min-h-[120px]"
                          {...field}
                          value={field.value || ''}
                          data-testid="notes"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-4 pb-8">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEdit ? 'Update Customer' : 'Create Customer'}
              </Button>
            </div>
          </form>
        </Form>

        {/* One-time prompt — "Upload a proposal to auto-fill?". Shown when
            the new-customer form first loads. Choosing "Upload now" opens
            the ProposalUploadCard's file picker; "Type it manually" just
            dismisses. The card itself remains pinned to the top of the
            form either way so admins can return to it at any point. */}
        {!isEdit && (
          <Dialog open={proposalPromptOpen} onOpenChange={setProposalPromptOpen}>
            <DialogContent className="sm:max-w-md" data-testid="new-customer-proposal-prompt">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-600" />
                  Got a Solar PV proposal handy?
                </DialogTitle>
                <DialogDescription>
                  Upload the customer's proposal PDF and we'll auto-fill the form below — name,
                  address, system size, panels, inverter, battery, total cost — plus the entire
                  Performance Estimate (MCS) section of the contract pack. Saves ~5 minutes per record.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => setProposalPromptOpen(false)}
                  data-testid="prompt-skip-btn"
                >
                  I'll type it manually
                </Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => {
                    setProposalPromptOpen(false);
                    setTimeout(() => proposalCardRef.current?.openPicker(), 100);
                  }}
                  data-testid="prompt-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-1.5" /> Upload proposal now
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* TTR Device Search Dialog */}
        <TTRDeviceSearchDialog
          open={!!ttrDialogTarget}
          onOpenChange={(open) => { if (!open) setTtrDialogTarget(null); }}
          onSelect={(device) => {
            if (ttrDialogTarget === 'inverter') {
              form.setValue('inverter_device_ref', device.DeviceRef);
              form.setValue('inverter_device_data', {
                DeviceID: device.DeviceID,
                DeviceRef: device.DeviceRef,
                TTRManufacturer: device.TTRManufacturer,
                ModelNo: device.ModelNo,
                TTRDeviceType: device.TTRDeviceType,
                RegisteredCapacity: device.RegisteredCapacity,
                TTRDevicePhase: device.TTRDevicePhase,
              });
              if (!form.getValues('inverter_manufacturer')) {
                form.setValue('inverter_manufacturer', device.TTRManufacturer);
              }
            } else if (ttrDialogTarget === 'battery') {
              form.setValue('battery_device_ref', device.DeviceRef);
              form.setValue('battery_device_data', {
                DeviceID: device.DeviceID,
                DeviceRef: device.DeviceRef,
                TTRManufacturer: device.TTRManufacturer,
                ModelNo: device.ModelNo,
                TTRDeviceType: device.TTRDeviceType,
                RegisteredCapacity: device.RegisteredCapacity,
                TTRDevicePhase: device.TTRDevicePhase,
              });
              if (!form.getValues('battery_manufacturer')) {
                form.setValue('battery_manufacturer', device.TTRManufacturer);
              }
            }
            setTtrDialogTarget(null);
            toast.success(`Device selected: ${device.TTRManufacturer} ${device.ModelNo}`);
          }}
        />

        {/* Post-create-customer prompt */}
        <PostCreateInvoiceDialog
          open={postCreateOpen}
          customer={createdCustomer}
          onClose={() => {
            setPostCreateOpen(false);
            setCreatedCustomer(null);
            navigate('/customers');
          }}
        />

        {/* Variation of Contract — triggered on equipment changes vs snapshot */}
        <VariationOfContractDialog
          open={variationDialogOpen}
          customer={variationCustomer}
          onOpenChange={(o) => {
            setVariationDialogOpen(o);
            if (!o) {
              // User dismissed without raising — navigate back to list
              setVariationCustomer(null);
              navigate('/customers');
            }
          }}
          onSaved={() => {
            setVariationDialogOpen(false);
            setVariationCustomer(null);
            navigate('/customers');
          }}
        />

        {/* Prompt when cost/equipment changed but there's no agreed contract yet */}
        <Dialog open={changePromptOpen} onOpenChange={(o) => { if (!o) setChangePromptOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="change-contract-prompt">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" /> System details changed
              </DialogTitle>
              <DialogDescription>
                You've changed the cost, equipment, model or quantity. Would you like to keep the
                customer's paperwork in step?
              </DialogDescription>
            </DialogHeader>
            {changeSummary.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5" data-testid="change-summary">
                {changeSummary.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 min-w-[110px]">{c.label}</span>
                    <span className="text-slate-400 line-through">{c.before}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-semibold text-slate-800">{c.after}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-2 pt-2">
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={() => { setChangePromptOpen(false); navigate(`/customers/${id}?action=variation`); }}
                data-testid="prompt-raise-variation-btn"
              >
                Raise a Variation of Contract
              </Button>
              <Button
                variant="outline"
                onClick={() => { setChangePromptOpen(false); navigate(`/customers/${id}?action=contract`); }}
                data-testid="prompt-new-contract-btn"
              >
                Create a New Contract
              </Button>
              {currentUser?.is_admin ? (
                <Button
                  variant="ghost"
                  className="text-slate-500"
                  onClick={() => { setChangePromptOpen(false); navigate('/customers'); }}
                  data-testid="prompt-skip-btn"
                >
                  Skip for now
                </Button>
              ) : (
                <p className="text-xs text-center text-slate-400 pt-1">
                  Please raise a variation or new contract. Ask an admin if this isn't needed.
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* EV charger sub-board prompt (battery systems) */}
        <Dialog open={evSubBoardOpen} onOpenChange={(o) => { if (!o) setEvSubBoardOpen(false); }}>
          <DialogContent className="sm:max-w-md" data-testid="ev-subboard-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Does this customer have an EV charger?
              </DialogTitle>
              <DialogDescription>
                This customer is having a battery installed. If they also have an
                <strong> EV charger</strong>, a separate sub-board is required —
                otherwise the car will drain the battery.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Adding the sub-board applies an extra <strong>£250</strong> to the
              System Total Cost and notes <strong>"Sub Board required for EV"</strong> in
              the Additional Extras box.
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleEvDismiss}
                data-testid="ev-subboard-dismiss-btn"
              >
                No / Dismiss
              </Button>
              <Button
                type="button"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={handleEvAddSubBoard}
                data-testid="ev-subboard-add-btn"
              >
                <Zap className="w-4 h-4 mr-2" />
                Add sub-board cost (£250)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Missing-inverter confirmation */}
        <Dialog open={inverterPromptOpen} onOpenChange={(o) => { if (!o) setInverterPromptOpen(false); }}>
          <DialogContent className="sm:max-w-md" data-testid="inverter-missing-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                No inverter entered — is that right?
              </DialogTitle>
              <DialogDescription>
                This system doesn't have an inverter added. You can still save without
                one, but we wanted to check it wasn't missed by accident.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleInverterGoBack}
                data-testid="inverter-goback-btn"
              >
                Go back &amp; add inverter
              </Button>
              <Button
                type="button"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={handleInverterConfirmSkip}
                data-testid="inverter-skip-btn"
              >
                Save without an inverter
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Name this job/project before creating a new customer */}
        <Dialog open={jobNameOpen} onOpenChange={(o) => { if (!o) setJobNameOpen(false); }}>
          <DialogContent className="sm:max-w-md" data-testid="job-name-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <House className="w-5 h-5 text-emerald-600" />
                Name this job
              </DialogTitle>
              <DialogDescription>
                Give this customer's first job a name. We've pre-filled the first line of the address —
                change it if you'd like. You can add more jobs later (Job 2, Job 3…).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <Input
                value={jobNameInput}
                onChange={(e) => setJobNameInput(e.target.value)}
                placeholder="e.g. 12 Solar Street"
                data-testid="job-name-input"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmJobName(); } }}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={confirmJobName}
                data-testid="job-name-confirm-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Create customer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Email changed — offer to resend contract + invoice to new address */}
        <Dialog open={emailResendOpen} onOpenChange={(o) => { if (!o && !emailResendBusy) handleResendNo(); }}>
          <DialogContent className="sm:max-w-md" data-testid="email-resend-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-sky-600" />
                Email address updated
              </DialogTitle>
              <DialogDescription>
                The customer's email address has changed. Would you like to send
                their latest <strong>contract</strong> and <strong>invoice</strong> to
                the new email address now?
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-800">
              The new details have also been pushed to Xero, and a portal
              registration email is sent automatically if the new address isn't
              already linked to a portal account.
            </div>
            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleResendNo}
                disabled={emailResendBusy}
                data-testid="email-resend-no-btn"
              >
                No, thanks
              </Button>
              <Button
                type="button"
                className="bg-sky-600 hover:bg-sky-700 text-white"
                onClick={handleResendYes}
                disabled={emailResendBusy}
                data-testid="email-resend-yes-btn"
              >
                {emailResendBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Send to new email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

function TTRDeviceSearchDialog({ open, onOpenChange, onSelect }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);
  const [mfrFilter, setMfrFilter] = useState('');
  const [mfrSearch, setMfrSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modelSearch, setModelSearch] = useState('');
  const [results, setResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (open && manufacturers.length === 0) {
      setLoadingRef(true);
      dnoAPI.getDeviceReference().then(res => {
        const data = res.data || {};
        setManufacturers((data.device_manufacturers || []).sort((a, b) => a.Manufacturer.localeCompare(b.Manufacturer)));
        setDeviceTypes(data.device_types || []);
      }).catch(() => {}).finally(() => setLoadingRef(false));
    }
    if (open) {
      setResults([]);
      setTotalResults(0);
      setMfrFilter('');
      setMfrSearch('');
      setModelSearch('');
      setTypeFilter('');
      setPage(0);
    }
  }, [open]);

  const doSearch = async (skip = 0) => {
    if (!mfrFilter && !modelSearch) {
      toast.error('Select a manufacturer or enter a model number');
      return;
    }
    setSearching(true);
    try {
      const params = { limit: 25, skip };
      if (mfrFilter) params.manufacturer_id = mfrFilter;
      if (typeFilter) params.device_type_id = typeFilter;
      if (modelSearch) params.query = modelSearch;
      const res = await dnoAPI.searchDevices(params);
      setResults(res.data?.data || []);
      setTotalResults(res.data?.total_results || 0);
      setPage(skip);
    } catch { toast.error('Search failed'); }
    finally { setSearching(false); }
  };

  const filteredMfrs = mfrSearch
    ? manufacturers.filter(m => m.Manufacturer.toLowerCase().includes(mfrSearch.toLowerCase())).slice(0, 50)
    : manufacturers.slice(0, 50);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-blue-600" />
            ENA Type Test Register — Device Search
          </DialogTitle>
          <DialogDescription>
            Search {totalResults > 0 ? `${totalResults.toLocaleString()} devices` : '8,000+ registered devices'}. Click a row to select.
          </DialogDescription>
        </DialogHeader>
        {loadingRef ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="flex flex-col gap-3 flex-1 overflow-hidden">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-slate-500">Manufacturer</label>
                <Input placeholder="Type to filter manufacturers..." value={mfrSearch} onChange={e => setMfrSearch(e.target.value)} data-testid="ttr-mfr-search" />
                {(mfrSearch || !mfrFilter) && filteredMfrs.length > 0 && (
                  <div className="max-h-28 overflow-y-auto border rounded-lg mt-1 divide-y text-sm bg-white">
                    {filteredMfrs.map(m => (
                      <div key={m.ManufacturerID}
                        className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 ${String(mfrFilter) === String(m.ManufacturerID) ? 'bg-blue-100 font-medium' : ''}`}
                        onClick={() => { setMfrFilter(String(m.ManufacturerID)); setMfrSearch(m.Manufacturer); }}
                      >{m.Manufacturer}</div>
                    ))}
                  </div>
                )}
                {mfrFilter && <Button type="button" variant="ghost" size="sm" className="text-xs mt-1 h-6 px-2" onClick={() => { setMfrFilter(''); setMfrSearch(''); }}>Clear manufacturer</Button>}
              </div>
              <div>
                <label className="text-xs text-slate-500">Model Number</label>
                <Input placeholder="e.g. H1-5.0" value={modelSearch} onChange={e => setModelSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch(0)} data-testid="ttr-model-search" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Device Type</label>
                <Select value={typeFilter || 'all'} onValueChange={v => setTypeFilter(v === 'all' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="All types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {deviceTypes.map(t => (<SelectItem key={t.DeviceTypeID} value={String(t.DeviceTypeID)}>{t.DeviceType}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="button" onClick={() => doSearch(0)} disabled={searching} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ttr-search-btn">
              {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Search Devices
            </Button>
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {results.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                  <Zap className="w-10 h-10 mb-2" />
                  <p className="text-sm">{searching ? 'Searching...' : 'Select a manufacturer and search'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 text-xs">
                      <TableHead>Manufacturer</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Capacity</TableHead>
                      <TableHead>Phase</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead className="w-[40px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {results.map(d => (
                      <TableRow key={d.DeviceID} className="cursor-pointer hover:bg-blue-50 text-sm" onClick={() => onSelect(d)}>
                        <TableCell className="font-medium">{d.TTRManufacturer}</TableCell>
                        <TableCell>{d.ModelNo}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{d.TTRDeviceType}</Badge></TableCell>
                        <TableCell>{d.RegisteredCapacity} kW</TableCell>
                        <TableCell>{d.TTRDevicePhase}ph</TableCell>
                        <TableCell><span className="font-mono text-xs text-slate-500">{d.DeviceRef}</span></TableCell>
                        <TableCell><ChevronRight className="w-4 h-4 text-slate-300" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            {totalResults > 25 && (
              <div className="flex items-center justify-between text-sm text-slate-500">
                <span>Showing {page + 1}–{Math.min(page + 25, totalResults)} of {totalResults.toLocaleString()}</span>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={page === 0 || searching} onClick={() => doSearch(Math.max(0, page - 25))}>Previous</Button>
                  <Button type="button" variant="outline" size="sm" disabled={page + 25 >= totalResults || searching} onClick={() => doSearch(page + 25)}>Next</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

